// NS-731: single implementation of the drag-reorder index walk, previously
// copied into Table.tsx (rows and columns), Activity's ColumnSelector and
// Activity's FilterSelector.

export type DragAxis = "x" | "y";

/**
 * Given the bounding boxes of the items as currently rendered, the index the
 * dragged item currently sits at and the pointer coordinate along `axis`,
 * returns the index the dragged item should move to.
 *
 * A neighbour larger than the dragged item is only passed once the pointer
 * clears the extra size difference ("forbidden area"), so dragging a small
 * item over a big one does not flip back and forth.
 */
export function computeDropIndex(boxes: DOMRect[], current: number, pointer: number, axis: DragAxis): number {
    const start = axis === "x" ? "left" : "top";
    const end = axis === "x" ? "right" : "bottom";
    const size = axis === "x" ? "width" : "height";

    const currentPos = boxes[current];
    let index = current;

    if (pointer < currentPos[start]) {
        //going up / left
        while (index > 0) {
            const otherPos = boxes[index - 1];
            const forbiddenArea = Math.max(otherPos[size] - currentPos[size], 0);
            if (pointer > (otherPos[end] - forbiddenArea)) {
                break;
            }
            index--;
        }
    } else {
        //going down / right
        while (index < (boxes.length - 1)) {
            const otherPos = boxes[index + 1];
            const forbiddenArea = Math.max(otherPos[size] - currentPos[size], 0);
            if (pointer < (otherPos[start] + forbiddenArea)) {
                break;
            }
            index++;
        }
    }

    return index;
}

export function moveItem<P>(arr: P[], i: number, finalPos: number) {
    while (i > finalPos) {
        [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
        i--;
    }

    while (i < finalPos) {
        [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
        i++;
    }
}
